import clsx from "clsx";
import { FunctionComponent } from "react";

import {
  type ContentCardArticle,
  ContentCardBaseConfig,
} from "@ui/components/content-cards/content-cards.types";
import ContentBlockAdvertiserFlag from "@ui/components/content-cards/ContentBlock/components/ContentBlockAdvertiserFlag/ContentBlockAdvertiserFlag";
import ContentBlockByline from "@ui/components/content-cards/ContentBlock/components/ContentBlockByline";
import ContentBlockCrossCommunityPost from "@ui/components/content-cards/ContentBlock/components/ContentBlockCrossCommunityPost";
import ContentBlockCrosspost from "@ui/components/content-cards/ContentBlock/components/ContentBlockCrosspost";
import ContentBlockLabel from "@ui/components/content-cards/ContentBlock/components/ContentBlockLabel";
import Category from "@ui/components/meta/Category";
import CommentCount from "@ui/components/meta/CommentCount";
import LiveBadge from "@ui/components/meta/LiveBadge";
import Timestamp from "@ui/components/meta/Timestamp";
import { useComponentConfig } from "@ui/context/component-config-context";
import useDefaults from "@ui/hooks/use-defaults";
import { useCommunityByWpSiteId } from "@voxmedia/duet-context/context/community-context";

import * as styles from "./ContentBlockMeta.css";

export type MetaVariation = "standard" | "special" | "box";

export type MetaComponent =
  | "ContentBlockByline"
  | "Category"
  | "Timestamp"
  | "ContentBlockAdvertiserFlag"
  | "CommentCount"
  | "ContentBlockMap"
  | "ContentBlockPackage"
  | "ContentBlockStream"
  | "ContentBlockCrossPost"
  | "ContentBlockCrossCommunityPost"
  | "LiveBadge"
  | undefined
  | null;

export interface ContentBlockMetaBaseConfig {
  defaultSeparators: {
    // [string, string] maps to first and second separator
    upper: [string, string];
    lower: [string, string];
  };
  metaVariation: MetaVariation;
  showAuthorBy: boolean;
  MapIcon: FunctionComponent<{ className?: string; fill?: string }>;
  showCommentCountText?: boolean;
}

interface Props
  extends Partial<ContentBlockMetaBaseConfig>, ContentCardArticle {
  componentContext?: string;
  firstComponent?: MetaComponent;
  layout: "upper" | "lower";
  liveBadgeOrientation?: "horizontal" | "vertical";
  secondComponent?: MetaComponent;
  thirdComponent?: MetaComponent;
  contentBlockSize?: ContentCardBaseConfig["contentBlockSize"];
  containerClassName?: string;
  categoryClassName?: string;
}

const ContentBlockMeta: FunctionComponent<Props> = (props) => {
  const contentBlockMeta = useComponentConfig(
    "contentBlockMeta",
    props.componentContext || "",
  );

  const {
    adFlag,
    advertiserName,
    advertiserPreamble,
    authors,
    authorByLine,
    category,
    commentCount,
    commentCountId,
    commentUrl,
    contentType,
    crossPostSource,
    firstComponent,
    headline,
    layout,
    live,
    liveBadgeName,
    defaultSeparators,
    MapIcon,
    metaVariation,
    packageName,
    secondComponent,
    showAuthorBy,
    site,
    streamName,
    thirdComponent,
    timestamp,
    contentBlockSize,
    componentContext,
    showCommentCountText,
    containerClassName,
    categoryClassName,
  } = useDefaults(props, contentBlockMeta);

  const communityData = useCommunityByWpSiteId(site?.wpId);
  const crossCommunityData =
    site?.isCrossCommunity && communityData ? communityData : null;

  const getComponent = (
    metaComponentName: MetaComponent,
    separator?: string,
  ) => {
    switch (metaComponentName) {
      case "ContentBlockByline":
        return (
          <ContentBlockByline
            authors={authors}
            authorByLine={authorByLine}
            showAuthorBy={showAuthorBy}
            className={separator}
            componentContext={componentContext}
          />
        );
      case "Category":
        return (
          category && (
            <Category
              category={category}
              metaVariation={metaVariation}
              className={clsx(separator, categoryClassName)}
            />
          )
        );
      case "Timestamp":
        return (
          timestamp && (
            <Timestamp
              timestamp={timestamp}
              className={separator}
              componentContext={componentContext}
            />
          )
        );
      case "ContentBlockAdvertiserFlag":
        return (
          adFlag && (
            <ContentBlockAdvertiserFlag
              advertiserName={advertiserName}
              advertiserPreamble={advertiserPreamble}
              className={separator}
            />
          )
        );
      case "CommentCount":
        return (
          commentUrl &&
          headline &&
          commentCountId && (
            <CommentCount
              url={commentUrl}
              commentCount={commentCount}
              authorName={authors[0]?.name || ""}
              headline={headline}
              contentType={contentType}
              className={separator}
              countId={commentCountId}
              variant="contentCard"
              noText={!showCommentCountText}
              componentContext={componentContext}
            />
          )
        );
      case "ContentBlockCrossCommunityPost":
        return (
          crossCommunityData && (
            <ContentBlockCrossCommunityPost
              source={crossCommunityData.name}
              metaVariation={metaVariation}
              className={separator}
            />
          )
        );
      case "ContentBlockCrossPost":
        return (
          crossPostSource && (
            <ContentBlockCrosspost
              source={crossPostSource}
              metaVariation={metaVariation}
              className={separator}
            />
          )
        );
      case "ContentBlockPackage":
        return (
          packageName && (
            <ContentBlockLabel
              label={packageName}
              metaVariation={metaVariation}
              className={clsx(styles.metaCategoryColor, separator)}
            />
          )
        );
      case "LiveBadge":
        return (
          live && (
            <LiveBadge
              variation="card"
              className={separator}
              label={liveBadgeName}
            />
          )
        );
      case "ContentBlockStream":
        return (
          streamName && (
            <ContentBlockLabel
              label={streamName}
              metaVariation={metaVariation}
              className={separator}
            />
          )
        );
      case "ContentBlockMap":
        return (
          <ContentBlockLabel
            label="Map"
            icon={<MapIcon className={styles.metaIcon} fill="currentColor" />}
            className={clsx(styles.metaCategoryColor, separator)}
          />
        );
    }
  };

  if (
    !getComponent(firstComponent) &&
    !getComponent(secondComponent) &&
    !getComponent(thirdComponent)
  ) {
    return null;
  }

  return (
    <div
      className={clsx(
        layout === "upper"
          ? styles.metaLayoutUpper[contentBlockSize || "medium"]
          : styles.metaLayoutLower[contentBlockSize || "medium"],
        containerClassName,
      )}
    >
      {firstComponent && <>{getComponent(firstComponent)}</>}
      {secondComponent && !adFlag && (
        <>
          {getComponent(
            secondComponent,
            getComponent(firstComponent)
              ? firstComponent === "LiveBadge"
                ? styles.pipelessSeparator
                : defaultSeparators[layout][0]
              : undefined,
          )}
        </>
      )}
      {thirdComponent && !adFlag && (
        <>
          {getComponent(
            thirdComponent,
            firstComponent || secondComponent
              ? defaultSeparators[layout][1]
              : undefined,
          )}
        </>
      )}
    </div>
  );
};

export default ContentBlockMeta;
